import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import intlTelInput from 'intl-tel-input';
import { FeeService } from '../service/fee.service';
import { EmergencyContactUpdateRequest, MemberProfileResponse } from '../interface/fee.interface';
import { DEFAULT_INITIAL_COUNTRY, DEFAULT_ITI_POLITE } from '../constants/global.constants';
import { ARM_PHONE_NUMBER_PATTERN } from '../constants/validation.constants';

export interface EditEmergencyContactDialogData {
  memberId: number;
  profile: MemberProfileResponse;
}

@Component({
  selector: 'app-fee-edit-emergency-contact-dialog',
  templateUrl: './fee-edit-emergency-contact-dialog.component.html',
  styleUrls: ['./fee-edit-emergency-contact-dialog.component.scss']
})
export class FeeEditEmergencyContactDialogComponent implements AfterViewInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private readonly ARMENIA_ISO = '374';

  @ViewChild('phoneInput') phoneInputRef?: ElementRef<HTMLInputElement>;
  private iti: intlTelInput.Plugin | null = null;
  invalidPhoneMessage: string | null = null;
  phoneTouched = false;

  form: FormGroup;
  saving = false;

  constructor(
    private readonly dialogRef: MatDialogRef<FeeEditEmergencyContactDialogComponent>,
    @Inject(MAT_DIALOG_DATA) readonly data: EditEmergencyContactDialogData,
    private readonly fb: FormBuilder,
    private readonly feeService: FeeService
  ) {
    this.form = this.fb.group({
      emergencyContactName: [data.profile.emergencyContactName || '', Validators.maxLength(100)],
      emergencyContactPhone: [data.profile.emergencyContactPhone || ''],
      emergencyContactEmail: [data.profile.emergencyContactEmail || '', Validators.email],
    });
  }

  ngAfterViewInit(): void {
    if (!this.phoneInputRef) return;
    this.iti = intlTelInput(this.phoneInputRef.nativeElement, {
      initialCountry: DEFAULT_INITIAL_COUNTRY,
      autoPlaceholder: DEFAULT_ITI_POLITE,
      separateDialCode: true,
      utilsScript: 'node_modules/intl-tel-input/build/js/utils.js'
    } as intlTelInput.Options);

    const existing = this.data.profile.emergencyContactPhone;
    if (existing) {
      this.iti.setNumber(existing);
    }
  }

  ngOnDestroy(): void {
    this.iti?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  focusPhoneInput(): void {
    this.phoneInputRef?.nativeElement.focus();
  }

  preventDefaultIfNotNumber(event: KeyboardEvent): void {
    const allowedChars = /[0-9]/;
    if (!allowedChars.test(event.key) && event.key !== 'Backspace') {
      event.preventDefault();
    }
  }

  private get phoneInputValue(): string {
    return (this.phoneInputRef?.nativeElement.value ?? '').trim();
  }

  get hasPhoneError(): boolean {
    const value = this.phoneInputValue;
    if (!value) return false;
    if (!this.iti) return false;
    const dial = this.iti.getSelectedCountryData()?.dialCode;
    if (dial === this.ARMENIA_ISO) {
      return !ARM_PHONE_NUMBER_PATTERN.test(value.replace(/\s+/g, ''));
    }
    return !this.iti.isValidNumber();
  }

  get isPhoneVisuallyInvalid(): boolean {
    return this.phoneTouched && this.hasPhoneError;
  }

  validatePhoneNumber(): void {
    this.phoneTouched = true;
    if (!this.phoneInputValue) {
      this.invalidPhoneMessage = null;
      return;
    }
    this.invalidPhoneMessage = this.hasPhoneError
      ? 'registration_content.valid_phone_number'
      : null;
  }

  onPhoneInput(): void {
    this.invalidPhoneMessage = null;
  }

  private getFormattedPhone(): string {
    const value = this.phoneInputValue;
    if (!value) return '';
    return this.iti?.getNumber() || value;
  }

  save(): void {
    if (this.form.invalid || this.saving) return;
    this.validatePhoneNumber();
    if (this.hasPhoneError) return;

    const raw = this.form.getRawValue();
    const request: EmergencyContactUpdateRequest = {
      emergencyContactName: raw.emergencyContactName || undefined,
      emergencyContactPhone: this.getFormattedPhone(),
      emergencyContactEmail: raw.emergencyContactEmail || undefined,
    };
    this.saving = true;
    this.feeService.updateEmergencyContact(this.data.memberId, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: updated => this.dialogRef.close(updated),
        error: () => { this.saving = false; }
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
